import type { BoltFxQuote } from "./boltutil-fx-types.js";
// Provider receipts remain USDT. Quoted new orders may separately credit CNY.
// The second route was observed in a real HTTPS create response on 2026-09-16.
export function safeBoltCheckoutUrl(value: unknown): string | undefined {
  if (typeof value !== "string") return;
  try {
    const url = new URL(value);
    const approved = (url.origin === "https://pay.boltutil.com" && /^\/checkout\/[A-Za-z0-9_-]{1,128}$/.test(url.pathname))
      || (url.origin === "https://boltutil.com" && /^\/zh\/checkout\/[A-Za-z0-9_-]{1,128}$/.test(url.pathname));
    if (approved && !url.username && !url.password && !url.search && !url.hash) return url.href;
  } catch { /* Not a verified checkout. */ }
}
export type BoltOrderStatus = "creating" | "pending" | "unknown" | "confirmed" | "expired";
export type BoltPayment = {
  address: string; amount: string; rawAmount: string; decimal: number;
  currency: "USDT"; network: "TRC20"; qrData: string;
};
export type BoltReceipt = {
  amount: string; paidAmount: string; shortfallAmount: string;
  settlementType: "EXACT" | "MANUAL_UNDERPAID";
  txHash: string; confirmations: number; destinationAddress: string;
  paidAt?: string; confirmedAt?: string; completedAt?: string;
  source: "webhook" | "query"; observedAt: string;
};
export type BoltOrder = {
  id: string; clientId: string; userId: string; username: string;
  requestedAmount: string; currency: "USDT"; network: "TRC20";
  destinationAddress: string;
  status: BoltOrderStatus; createdAt: string; updatedAt: string;
  checkoutUrl?: string; payment?: BoltPayment; expireTimestamp?: number;
  receipt?: BoltReceipt; issue?: string;
  deferredReceipt?: BoltReceipt;
  review?: { note: string; reviewedAt: string; reviewedBy: string };
  quote?: BoltFxQuote;
  settlementExpectedCents?: string;
  credit?: {
    transactionId: string; amountCents: number; currency: "CNY";
    creditedAt: string; paidUsdt: string; rate: string; policyVersion: "bolt-fx-v1" | "bolt-legacy-approved-v1";
  };
  legacyApproval?: { rate: string; approvedBy: string; approvedAt: string; txHash: string; noPriorCreditConfirmed: true };
  settlementIssue?: string;
};
export type BoltPublicOrder = Omit<BoltOrder, "userId" | "username" | "review" | "deferredReceipt" | "destinationAddress" | "legacyApproval"> & {
  reviewedAt?: string;
};
export type BoltAvailability = {
  enabled: boolean; currency: "USDT"; network: "TRC20";
  minAmount: "1"; manualReviewOnly: boolean; autoCreditEnabled?: boolean;
};
export type BoltAdminConfiguration = BoltAvailability & {
  apiKeyConfigured: boolean; webhookSecretConfigured: boolean;
  destinationConfigured: boolean; callbackUrl: string;
};
export type BoltMemberResponse = { configuration: BoltAvailability; orders: BoltPublicOrder[]; total?: number; nextOffset?: number };
export type BoltAdminResponse = { configuration: BoltAdminConfiguration; orders: BoltOrder[]; total?: number; nextOffset?: number };

export type BoltLegacyPreview = { orderId: string; rate: string; paidUsdt: string; txHash: string; amountCents: number; alreadyCredited: boolean; blockers: string[] };
